import React, { useState, useEffect, useCallback } from 'react';
import { MapContainer, TileLayer, CircleMarker, Popup, useMap } from 'react-leaflet';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from '../components/ui/sheet';
import {
  Map, TrendingUp, DollarSign, Truck, MapPin, Activity,
  Clock, Filter, X, ChevronRight, BarChart3, Loader2, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import 'leaflet/dist/leaflet.css';

// Heatmap layer component
const HeatmapLayer = ({ regions }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!regions || regions.length === 0) return;
    
    // Import leaflet.heat dynamically
    import('leaflet.heat').then((L) => {
      const heatData = regions.map(r => [r.lat, r.lng, r.intensity * 100]);
      
      const heat = L.default ? L.default.heatLayer(heatData, {
        radius: 50,
        blur: 30,
        maxZoom: 10,
        gradient: {
          0.2: '#0D1B2A',
          0.4: '#3B82F6',
          0.6: '#60A5FA',
          0.8: '#4ADE80',
          1.0: '#86EFAC'
        }
      }) : window.L.heatLayer(heatData, {
        radius: 50,
        blur: 30,
        maxZoom: 10,
        gradient: {
          0.2: '#0D1B2A',
          0.4: '#3B82F6',
          0.6: '#60A5FA',
          0.8: '#4ADE80',
          1.0: '#86EFAC'
        }
      });
      
      heat.addTo(map);
      
      return () => {
        map.removeLayer(heat);
      };
    }).catch(err => console.error('Failed to load heatmap:', err));
  }, [map, regions]);
  
  return null;
};

const IntelMapPage = () => {
  const { isAuthenticated, profile } = useAuth();
  
  // Data states
  const [marketSnapshot, setMarketSnapshot] = useState(null);
  const [regions, setRegions] = useState([]);
  const [topRoutes, setTopRoutes] = useState([]);
  const [hotMarkets, setHotMarkets] = useState([]);
  const [selectedRegion, setSelectedRegion] = useState(null);
  const [regionDetails, setRegionDetails] = useState(null);
  
  // UI states
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [loadingRegion, setLoadingRegion] = useState(false);
  
  // Filters
  const [timeRange, setTimeRange] = useState('30d');
  const [vehicleType, setVehicleType] = useState('all');
  
  // Auto-refresh
  const [lastUpdate, setLastUpdate] = useState(new Date());

  const loadData = useCallback(async () => {
    if (!isAuthenticated) return;
    
    try {
      const params = { time_range: timeRange };
      if (vehicleType !== 'all') {
        params.vehicle_type = vehicleType;
      }
      
      const [snapshotRes, regionsRes, routesRes] = await Promise.all([
        api.get('/intel/market-snapshot', { params }),
        api.get('/intel/regions', { params }),
        api.get('/intel/top-routes', { params: { ...params, limit: 10 } })
      ]);
      
      setMarketSnapshot(snapshotRes.data);
      setRegions(regionsRes.data);
      setTopRoutes(routesRes.data);
      
      // Calculate hot markets from regions (top 5 by load count in last 24h)
      const sortedRegions = [...(regionsRes.data || [])].sort((a, b) => b.load_count - a.load_count);
      setHotMarkets(sortedRegions.slice(0, 5));
      
      setLastUpdate(new Date());
    } catch (error) {
      console.error('Failed to load intel data:', error);
      toast.error('Failed to load market data');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, timeRange, vehicleType]);

  useEffect(() => {
    loadData();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(loadData, 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  const handleRegionClick = async (region) => {
    setSelectedRegion(region);
    setSidebarOpen(true);
    setLoadingRegion(true);
    
    try {
      const response = await api.get(`/intel/region/${region.state}`, {
        params: { time_range: timeRange }
      });
      setRegionDetails(response.data);
    } catch (error) {
      toast.error('Failed to load region details');
    } finally {
      setLoadingRegion(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(value);
  };

  // Get color based on intensity - glowing cyan/teal theme
  const getMarkerColor = (intensity) => {
    if (intensity > 0.7) return '#00FFFF'; // Bright cyan for high
    if (intensity > 0.4) return '#22D3EE'; // Cyan for medium
    if (intensity > 0.2) return '#06B6D4'; // Teal for low-medium
    return '#0891B2'; // Dark teal for low
  };

  // Get glow intensity based on load count
  const getGlowIntensity = (intensity) => {
    if (intensity > 0.7) return '0 0 20px #00FFFF, 0 0 40px #00FFFF, 0 0 60px #22D3EE';
    if (intensity > 0.4) return '0 0 15px #22D3EE, 0 0 30px #06B6D4';
    if (intensity > 0.2) return '0 0 10px #06B6D4, 0 0 20px #0891B2';
    return '0 0 8px #0891B2';
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A1628] via-[#0D1B2A] to-[#081424] pt-20 flex items-center justify-center">
        <Card className="bg-[#112240] border-[#3B82F6]/20 max-w-md" style={{boxShadow: '0 8px 32px rgba(59, 130, 246, 0.15)'}}>
          <CardContent className="pt-6 text-center">
            <Map className="w-16 h-16 text-[#4ADE80] mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Transport Intel Map</h2>
            <p className="text-[#94A3B8] mb-4">Sign in to access market intelligence</p>
            <Button 
              className="bg-gradient-to-r from-[#4ADE80] to-[#22C55E] text-black hover:from-[#86EFAC] hover:to-[#4ADE80] font-semibold"
              style={{boxShadow: '0 4px 16px rgba(74, 222, 128, 0.4)'}}
              onClick={() => window.location.href = '/login'}
            >
              Sign In
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-[#0A1628] via-[#0D1B2A] to-[#081424] pt-20 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#4ADE80] animate-spin mx-auto mb-4" />
          <p className="text-[#94A3B8]">Loading market intelligence...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0A1628] via-[#0D1B2A] to-[#081424] pt-16">
      {/* Header */}
      <div className="bg-[#0D1B2A]/95 border-b border-[#3B82F6]/20 px-4 py-4" style={{boxShadow: '0 4px 30px rgba(59, 130, 246, 0.1)'}}>
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#3B82F6]/20 to-[#4ADE80]/20 flex items-center justify-center">
                <Map className="w-6 h-6 text-[#4ADE80]" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white" style={{fontFamily: 'Poppins, sans-serif'}}>Transport Intel Map</h1>
                <p className="text-sm text-[#64748B]">Real-time logistics intelligence</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-xs text-[#64748B]">
              <Clock className="w-4 h-4" />
              Updated: {lastUpdate.toLocaleTimeString()}
              <Button variant="ghost" size="sm" onClick={loadData} className="text-[#60A5FA] hover:text-[#93C5FD] hover:bg-[#3B82F6]/10">
                <RefreshCw className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Market Snapshot */}
          {marketSnapshot && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <Card className="bg-[#112240] border-[#3B82F6]/20" style={{boxShadow: '0 4px 16px rgba(59, 130, 246, 0.1)'}}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-[#94A3B8] text-xs mb-1">
                    <Activity className="w-4 h-4 text-[#60A5FA]" />
                    Loads Today
                  </div>
                  <div className="text-2xl font-bold text-white">{marketSnapshot.loads_today}</div>
                </CardContent>
              </Card>
              <Card className="bg-[#112240] border-[#3B82F6]/20" style={{boxShadow: '0 4px 16px rgba(59, 130, 246, 0.1)'}}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-[#94A3B8] text-xs mb-1">
                    <DollarSign className="w-4 h-4 text-[#4ADE80]" />
                    Avg Payout
                  </div>
                  <div className="text-2xl font-bold text-[#4ADE80]">{formatCurrency(marketSnapshot.avg_payout)}</div>
                </CardContent>
              </Card>
              <Card className="bg-[#112240] border-[#3B82F6]/20" style={{boxShadow: '0 4px 16px rgba(59, 130, 246, 0.1)'}}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-[#94A3B8] text-xs mb-1">
                    <TrendingUp className="w-4 h-4 text-[#FB923C]" />
                    Hottest Region
                  </div>
                  <div className="text-2xl font-bold text-white">{marketSnapshot.hottest_region}</div>
                </CardContent>
              </Card>
              <Card className="bg-[#112240] border-[#3B82F6]/20" style={{boxShadow: '0 4px 16px rgba(59, 130, 246, 0.1)'}}>
                <CardContent className="p-4">
                  <div className="flex items-center gap-2 text-[#94A3B8] text-xs mb-1">
                    <Truck className="w-4 h-4 text-[#22D3EE]" />
                    Top Vehicle
                  </div>
                  <div className="text-2xl font-bold text-white truncate">{marketSnapshot.most_common_vehicle}</div>
                </CardContent>
              </Card>
            </div>
          )}

          {/* Filters */}
          <div className="flex items-center gap-3 relative z-[1000]">
            <div className="flex items-center gap-2 text-[#94A3B8]">
              <Filter className="w-4 h-4" />
              <span className="text-sm">Filters:</span>
            </div>
            <Select value={timeRange} onValueChange={setTimeRange}>
              <SelectTrigger className="w-32 bg-[#112240] border-[#3B82F6]/20 text-white">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#112240] border-[#3B82F6]/20 z-[1001]">
                <SelectItem value="24h">Last 24 Hours</SelectItem>
                <SelectItem value="7d">Last 7 Days</SelectItem>
                <SelectItem value="30d">Last 30 Days</SelectItem>
              </SelectContent>
            </Select>
            <Select value={vehicleType} onValueChange={setVehicleType}>
              <SelectTrigger className="w-40 bg-[#112240] border-[#3B82F6]/20 text-white">
                <SelectValue placeholder="Vehicle Type" />
              </SelectTrigger>
              <SelectContent className="bg-[#112240] border-[#3B82F6]/20 z-[1001]">
                <SelectItem value="all">All Vehicles</SelectItem>
                <SelectItem value="sedan">Sedan</SelectItem>
                <SelectItem value="suv">SUV</SelectItem>
                <SelectItem value="truck">Truck</SelectItem>
                <SelectItem value="van">Van</SelectItem>
                <SelectItem value="motorcycle">Motorcycle</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      {/* Map Container */}
      <div className="flex h-[calc(100vh-280px)]">
        {/* Map */}
        <div className="flex-1 relative z-0">
          <MapContainer
            center={[45.0, -100.0]}
            zoom={4}
            minZoom={3}
            maxZoom={10}
            maxBounds={[[15, -170], [72, -50]]}
            maxBoundsViscosity={1.0}
            style={{ height: '100%', width: '100%', background: '#0A1628' }}
            zoomControl={false}
          >
            <TileLayer
              url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            />
            
            {/* Region markers with glow effect */}
            {regions.map((region) => {
              const markerColor = getMarkerColor(region.intensity);
              const size = Math.max(12, Math.min(50, region.load_count * 3));
              return (
                <CircleMarker
                  key={region.state}
                  center={[region.lat, region.lng]}
                  radius={size / 2}
                  pathOptions={{
                    color: markerColor,
                    fillColor: markerColor,
                    fillOpacity: 0.8,
                    weight: 3,
                    className: 'glow-marker'
                  }}
                  eventHandlers={{
                    click: () => handleRegionClick(region)
                  }}
                >
                  <Popup className="intel-popup">
                    <div className="text-center p-1">
                      <div className="font-bold text-[#00FFFF]">{region.name}</div>
                      <div className="text-sm text-gray-300">{region.load_count} loads</div>
                      <div className="text-sm text-[#4ADE80] font-semibold">{formatCurrency(region.avg_price)} avg</div>
                    </div>
                  </Popup>
                </CircleMarker>
              );
            })}
          </MapContainer>

          {/* Legend */}
          <div className="absolute bottom-4 left-4 bg-[#112240]/95 border border-[#22D3EE]/30 rounded-lg p-3 z-[1000]" style={{boxShadow: '0 4px 16px rgba(34, 211, 238, 0.2)'}}>
            <div className="text-xs text-[#94A3B8] mb-2">Load Activity</div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{backgroundColor: '#0891B2', boxShadow: '0 0 6px #0891B2'}} />
                <span className="text-xs text-[#64748B]">Low</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{backgroundColor: '#22D3EE', boxShadow: '0 0 8px #22D3EE'}} />
                <span className="text-xs text-[#64748B]">Medium</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 rounded-full" style={{backgroundColor: '#00FFFF', boxShadow: '0 0 12px #00FFFF, 0 0 20px #22D3EE'}} />
                <span className="text-xs text-[#64748B]">High</span>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar with Hot Markets and Top Routes */}
        <div className="w-80 bg-[#0D1B2A] border-l border-[#3B82F6]/20 overflow-y-auto hidden lg:block">
          <div className="p-4">
            {/* Hot Markets Today Widget */}
            <div className="mb-6">
              <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-4" style={{fontFamily: 'Poppins, sans-serif'}}>
                <TrendingUp className="w-4 h-4 text-[#EF4444]" />
                Hot Markets Today
              </h3>
              <div className="space-y-2">
                {hotMarkets.map((market, idx) => (
                  <div
                    key={market.state || idx}
                    onClick={() => handleRegionClick(market)}
                    className={`bg-[#112240] border rounded-lg p-3 cursor-pointer transition-all group hover:border-[#4ADE80]/50 ${
                      idx === 0 ? 'border-l-4 border-l-[#EF4444] border-[#EF4444]/30' :
                      idx === 1 ? 'border-l-4 border-l-[#FB923C] border-[#FB923C]/30' :
                      idx === 2 ? 'border-l-4 border-l-[#FBBF24] border-[#FBBF24]/30' :
                      'border-[#3B82F6]/20'
                    }`}
                    style={{boxShadow: idx < 3 ? `0 0 16px ${idx === 0 ? 'rgba(239, 68, 68, 0.15)' : idx === 1 ? 'rgba(251, 146, 60, 0.15)' : 'rgba(251, 191, 36, 0.15)'}` : 'none'}}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className={`text-lg font-bold ${
                          idx === 0 ? 'text-[#EF4444]' : 
                          idx === 1 ? 'text-[#FB923C]' : 
                          idx === 2 ? 'text-[#FBBF24]' : 'text-[#64748B]'
                        }`}>
                          #{idx + 1}
                        </span>
                        <div>
                          <div className="text-white font-medium group-hover:text-[#4ADE80] transition-colors">
                            {market.name || market.state}
                          </div>
                          <div className="text-xs text-[#64748B]">
                            {market.load_count} loads today
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm text-[#4ADE80] font-medium">
                          {formatCurrency(market.avg_price)}
                        </div>
                        <div className="text-xs text-[#64748B]">avg rate</div>
                      </div>
                    </div>
                    {/* Activity bar */}
                    <div className="mt-2 h-1 bg-[#0A1628] rounded-full overflow-hidden">
                      <div 
                        className={`h-full rounded-full ${
                          idx === 0 ? 'bg-[#EF4444]' : 
                          idx === 1 ? 'bg-[#FB923C]' : 
                          idx === 2 ? 'bg-[#FBBF24]' : 'bg-[#4ADE80]'
                        }`}
                        style={{ width: `${Math.min(100, (market.intensity || 0.5) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
                {hotMarkets.length === 0 && (
                  <div className="text-center text-[#64748B] text-sm py-4">
                    No market data available
                  </div>
                )}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t border-[#3B82F6]/20 my-4" />

            {/* Top Routes Section */}
            <h3 className="text-sm font-semibold text-white flex items-center gap-2 mb-4" style={{fontFamily: 'Poppins, sans-serif'}}>
              <BarChart3 className="w-4 h-4 text-[#4ADE80]" />
              Top Routes
            </h3>
            <div className="space-y-2">
              {topRoutes.map((route, idx) => (
                <div
                  key={idx}
                  className="bg-[#112240] border border-[#3B82F6]/20 rounded-lg p-3 hover:border-[#3B82F6]/40 transition-colors"
                >
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-[#4ADE80] font-medium">#{idx + 1}</span>
                    <span className="text-xs text-[#64748B]">{route.count} loads</span>
                  </div>
                  <div className="text-sm text-white">{route.route}</div>
                  <div className="text-xs text-[#4ADE80] mt-1">{formatCurrency(route.avg_price)} avg</div>
                </div>
              ))}
              {topRoutes.length === 0 && (
                <div className="text-center text-[#64748B] text-sm py-8">
                  No route data available
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Region Details Sheet */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent className="bg-[#112240] border-[#3B82F6]/20 w-full sm:max-w-lg" style={{boxShadow: '-8px 0 32px rgba(59, 130, 246, 0.2)'}}>
          <SheetHeader>
            <SheetTitle className="text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-[#4ADE80]" />
              {selectedRegion?.name}
            </SheetTitle>
            <SheetDescription className="text-[#94A3B8]">
              Region statistics and top routes
            </SheetDescription>
          </SheetHeader>
          
          {loadingRegion ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 text-[#4ADE80] animate-spin" />
            </div>
          ) : regionDetails ? (
            <div className="mt-6 space-y-6">
              {/* Region Stats */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-[#0D1B2A] border border-[#3B82F6]/20 rounded-lg p-4">
                  <div className="text-xs text-[#94A3B8] mb-1">Total Loads</div>
                  <div className="text-2xl font-bold text-white">{regionDetails.total_loads}</div>
                </div>
                <div className="bg-[#0D1B2A] border border-[#3B82F6]/20 rounded-lg p-4">
                  <div className="text-xs text-[#94A3B8] mb-1">Avg Payout</div>
                  <div className="text-2xl font-bold text-[#4ADE80]">{formatCurrency(regionDetails.avg_payout)}</div>
                </div>
              </div>

              {/* Top Vehicle Type */}
              <div className="bg-[#0D1B2A] border border-[#3B82F6]/20 rounded-lg p-4">
                <div className="flex items-center gap-2 text-[#94A3B8] text-xs mb-2">
                  <Truck className="w-4 h-4 text-[#60A5FA]" />
                  Most Common Vehicle
                </div>
                <div className="text-lg font-semibold text-white">{regionDetails.top_vehicle_type}</div>
              </div>

              {/* Vehicle Breakdown */}
              {regionDetails.vehicle_breakdown && Object.keys(regionDetails.vehicle_breakdown).length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-white mb-3">Vehicle Breakdown</h4>
                  <div className="space-y-2">
                    {Object.entries(regionDetails.vehicle_breakdown).map(([type, count]) => (
                      <div key={type} className="flex items-center justify-between">
                        <span className="text-sm text-[#94A3B8]">{type}</span>
                        <span className="text-sm text-white">{count} loads</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Top Routes */}
              {regionDetails.top_routes && regionDetails.top_routes.length > 0 && (
                <div>
                  <h4 className="text-sm font-semibold text-white mb-3">Top Routes from {regionDetails.name}</h4>
                  <div className="space-y-2">
                    {regionDetails.top_routes.map((route, idx) => (
                      <div
                        key={idx}
                        className="flex items-center justify-between bg-[#0D1B2A] border border-[#3B82F6]/20 rounded-lg p-3"
                      >
                        <div className="flex items-center gap-2">
                          <ChevronRight className="w-4 h-4 text-[#4ADE80]" />
                          <span className="text-sm text-white">{route.route}</span>
                        </div>
                        <span className="text-xs text-[#64748B]">{route.count} loads</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* View Loads Button */}
              <Button
                className="w-full bg-gradient-to-r from-[#4ADE80] to-[#22C55E] text-black hover:from-[#86EFAC] hover:to-[#4ADE80] font-semibold"
                style={{boxShadow: '0 4px 16px rgba(74, 222, 128, 0.4)'}}
                onClick={() => {
                  setSidebarOpen(false);
                  window.location.href = `/loadboard?pickup=${regionDetails.name}`;
                }}
              >
                View Loads from {regionDetails.name}
              </Button>
            </div>
          ) : (
            <div className="text-center text-[#64748B] py-12">
              Select a region on the map
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default IntelMapPage;
